/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2025. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2025. Todos los derechos reservados.
 */


/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         6/04/25
 * Module name:  toast.service
 * File name:    toast.service.ts
 * IDE:          WebStorm
 */


import {inject, Injectable} from '@angular/core';
import {NbToastrService} from '@nebular/theme';
import {GenericKeyInterface, ZbStatusEnum, ZbStatusType, ZbStatusValidExp, ZbToastContentInterface} from '@zibanu/ui';
import {HttpErrorResponse} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class MkadToastService {
  private readonly toast = inject(NbToastrService);
  protected toastIconControl: GenericKeyInterface = {
    'success': 'checkmark-outline',
    'primary': 'bell-outline',
    'control': 'bell-outline',
    'info': 'bell-outline',
    'warning': 'alert-triangle-outline',
    'danger': 'close-outline',
  };

  /**
   * @description
   * Method that triggers the toast according to types (severity)
   * @param severity  toast type
   * @param content message content
   * @protected
   */
  showToast(severity: ZbStatusType, content: ZbToastContentInterface): void {
    if (!content) return;
    if (typeof content.message == 'string') {
      content.message = [content.message];
    }
    if (Object.keys(ZbStatusEnum).some(ZbStatusValidExp(severity))) {
      content.message.forEach(
        (message: string) => {
          this.toast.show(undefined, message, {
              status: severity,
              icon: {
                icon: this.toastIconControl[severity],
                pack: 'eva',
              },
              toastClass: content.cssClass,
            },
          );
        },
      );
    } else {
      this.toast.control(content.title, content.message);
    }
  }

  /**
   * @description
   * Method showing a custom types toast
   * @param content message content
   * @param cssClass css class for  content
   * @protected
   */
  showCustomToast(content: string | string[], cssClass?: string): void {
    this.showToast(ZbStatusEnum.control, {message: content, cssClass: cssClass});
  }

  /**
   * @description
   * Method showing a success types toast
   * @param content message content
   * @protected
   */
  showSuccess(content: string | string[]): void {
    this.showToast(ZbStatusEnum.success, {message: content});
  }

  /**
   * @description
   * Method showing an info types toast
   * @param content message content
   * @protected
   */
  showInfo(content: string | string[]): void {
    this.showToast(ZbStatusEnum.info, {message: content});
  }

  /**
   * @description
   * Method showing a primary types toast
   * @param content message content
   * @protected
   */
  showPrimary(content: string | string[]): void {
    this.showToast(ZbStatusEnum.primary, {message: content});
  }

  /**
   * @description
   * Method showing a warning types toast
   * @param content message content
   * @protected
   */
  showWarning(content: string | string[]): void {
    this.showToast(ZbStatusEnum.warning, {message: content});
  }

  /**
   * @description
   * Method showing a danger types toast
   * @param content message content
   * @protected
   */
  showError(content: string | string[]): void {
    if (!content) {
      return;
    }
    this.showToast(ZbStatusEnum.danger, {message: content, title: 'Error'});
  }

  /**
   * @description
   * Method showing a basic types toast
   * @param content message content
   * @protected
   */
  showBasic(content: string | string[]): void {
    this.showToast(ZbStatusEnum.control, {message: content});
  }

  /**
   * @description
   * Method showing an alert types toast
   * @param content message content
   * @protected
   */
  showAlert(content: string | string[]): void {
    this.showCustomToast(content, 'toast__alert');
  }

  /**
   * @description
   * Method to display toast http error type
   * @param param message content
   */
  showHttpError(param: HttpErrorResponse): void {
    this.showError(param.error ? param.error.errors : [param.error]);
  }

}
